import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Database,
  BarChart3,
  Users,
  FileText,
  TrendingUp,
  Download,
  Eye,
  Calendar,
  Loader2,
  Info
} from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';

interface AssessmentResult {
  id: string;
  project_id: string;
  project_name: string;
  user_email: string;
  completion_percentage: number;
  module_type: string;
  completed_at: string;
  overall_score: number;
}

export default function AssessmentDataScreen() {
  const [activeTab, setActiveTab] = useState("overview");
  const [filterModule, setFilterModule] = useState<string>('all');
  const [filterDateRange, setFilterDateRange] = useState<string>('30d');

  // Fetch assessment analytics
  const { data: assessmentStats } = useQuery({
    queryKey: ['/api/admin/assessment-stats'],
    queryFn: () => apiRequest('/api/admin/assessment-stats'),
    staleTime: 5 * 60 * 1000
  });

  // Fetch recent assessments
  const { data: recentAssessments = [], isLoading: assessmentsLoading } = useQuery({
    queryKey: ['/api/admin/recent-assessments'],
    queryFn: () => apiRequest('/api/admin/recent-assessments')
  });

  // Fetch maturity results
  const { data: maturityResults = [], isLoading: maturityLoading } = useQuery({
    queryKey: ['/api/admin/maturity-results'],
    queryFn: () => apiRequest('/api/admin/maturity-results')
  });

  // Fetch value assessment results
  const { data: valueResults = [], isLoading: valueLoading } = useQuery({
    queryKey: ['/api/admin/value-assessment-results'],
    queryFn: () => apiRequest('/api/admin/value-assessment-results')
  });

  const moduleTypes = ['Maturity Assessment', 'Value Assessment', 'ROI Analysis', 'Benchmarking'];
  const dateRanges = [
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 90 days' },
    { value: '1y', label: 'Last year' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Assessment Data & Analytics</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          View and analyze assessment results, completion rates, and user engagement
        </p>
      </div>

      {/* Analytics Guidelines */}
      <Card className="border-purple bg-purple dark:bg-purple/20">
        <CardHeader>
          <CardTitle className="flex items-center text-purple dark:text-purple">
            <Info className="w-5 h-5 mr-2" />
            Assessment Data Overview
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div>
              <h4 className="font-medium text-purple dark:text-purple mb-2">Data Sources:</h4>
              <ul className="space-y-1 text-purple dark:text-purple">
                <li>• <strong>Maturity Results:</strong> Assessment scores and recommendations</li>
                <li>• <strong>Value Assessments:</strong> Step completion and configuration data</li>
                <li>• <strong>ROI Results:</strong> Financial projections and calculations</li>
                <li>• <strong>Benchmarking:</strong> Industry comparison data</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-purple dark:text-purple mb-2">Key Metrics:</h4>
              <ul className="space-y-1 text-purple dark:text-purple">
                <li>• Completion rates by module</li>
                <li>• Average assessment scores</li>
                <li>• User engagement patterns</li>
                <li>• Drop-off points and bottlenecks</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-purple dark:text-purple mb-2">Use Cases:</h4>
              <ul className="space-y-1 text-purple dark:text-purple">
                <li>• Identify content improvement opportunities</li>
                <li>• Monitor platform usage and adoption</li>
                <li>• Support client success initiatives</li>
                <li>• Validate assessment effectiveness</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="maturity">Maturity Assessments</TabsTrigger>
          <TabsTrigger value="value">Value Assessments</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          {/* Key Metrics */}
          <div className="grid md:grid-cols-4 gap-6 mb-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Assessments</CardTitle>
                <Database className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{assessmentStats?.total_assessments || 0}</div>
                <p className="text-xs text-muted-foreground">All modules combined</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Completed</CardTitle>
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{assessmentStats?.completed_assessments || 0}</div>
                <p className="text-xs text-muted-foreground">
                  {assessmentStats?.completion_rate || 0}% completion rate
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active Users</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{assessmentStats?.active_users || 0}</div>
                <p className="text-xs text-muted-foreground">Last 30 days</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Avg Score</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{assessmentStats?.average_score || 0}</div>
                <p className="text-xs text-muted-foreground">Across all assessments</p>
              </CardContent>
            </Card>
          </div>

          {/* Recent Activity */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Recent Assessment Activity</CardTitle>
                  <CardDescription>Latest assessment completions and submissions</CardDescription>
                </div>
                <div className="flex space-x-2">
                  <Select value={filterModule} onValueChange={setFilterModule}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="All Modules" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Modules</SelectItem>
                      {moduleTypes.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={filterDateRange} onValueChange={setFilterDateRange}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {dateRanges.map(range => (
                        <SelectItem key={range.value} value={range.value}>{range.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {assessmentsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                  <span className="ml-2">Loading recent assessments...</span>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Project</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Module</TableHead>
                      <TableHead>Completion</TableHead>
                      <TableHead>Score</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recentAssessments.slice(0, 10).map((assessment: AssessmentResult) => (
                      <TableRow key={assessment.id}>
                        <TableCell className="font-medium">{assessment.project_name}</TableCell>
                        <TableCell>{assessment.user_email}</TableCell>
                        <TableCell>{assessment.module_type}</TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-blue-600 h-2 rounded-full" 
                                style={{ width: `${assessment.completion_percentage}%` }}
                              ></div>
                            </div>
                            <span className="text-xs text-gray-600">
                              {assessment.completion_percentage}%
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>{assessment.overall_score || 'N/A'}</TableCell>
                        <TableCell>{new Date(assessment.completed_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Button variant="outline" size="sm">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="maturity">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Maturity Assessment Results</CardTitle>
                  <CardDescription>Detailed view of maturity assessment data and scores</CardDescription>
                </div>
                <Button variant="outline">
                  <Download className="w-4 h-4 mr-2" />
                  Export Data
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {maturityLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                  <span className="ml-2">Loading maturity results...</span>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Project</TableHead>
                      <TableHead>Completion %</TableHead>
                      <TableHead>Overall Score</TableHead>
                      <TableHead>Quadrant</TableHead>
                      <TableHead>Completed</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {maturityResults.slice(0, 10).map((result: any) => (
                      <TableRow key={result.id}>
                        <TableCell className="font-medium">{result.project_name || 'Unknown Project'}</TableCell>
                        <TableCell>
                          <span className="font-medium text-green-600">{result.completion_percentage}%</span>
                        </TableCell>
                        <TableCell>{result.overall_score || 'N/A'}</TableCell>
                        <TableCell>{result.quadrant || 'Not Assessed'}</TableCell>
                        <TableCell>{result.completed_at ? new Date(result.completed_at).toLocaleDateString() : 'In Progress'}</TableCell>
                        <TableCell>
                          <Button variant="outline" size="sm">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="value">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Value Assessment Results</CardTitle>
                  <CardDescription>Configuration and completion data for value assessments</CardDescription>
                </div>
                <Button variant="outline">
                  <Download className="w-4 h-4 mr-2" />
                  Export Data
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {valueLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                  <span className="ml-2">Loading value assessment results...</span>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Project</TableHead>
                      <TableHead>Current Step</TableHead>
                      <TableHead>Completion %</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Last Updated</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {valueResults.slice(0, 10).map((result: any) => (
                      <TableRow key={result.id}>
                        <TableCell className="font-medium">{result.project_name || 'Unknown Project'}</TableCell>
                        <TableCell>Step {result.current_step || 1}/6</TableCell>
                        <TableCell>
                          <span className="font-medium text-blue-600">{result.completion_percentage || 0}%</span>
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            result.is_complete ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {result.is_complete ? 'Complete' : 'In Progress'}
                          </span>
                        </TableCell>
                        <TableCell>{result.updated_at ? new Date(result.updated_at).toLocaleDateString() : 'N/A'}</TableCell>
                        <TableCell>
                          <Button variant="outline" size="sm">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Module Performance</CardTitle>
                <CardDescription>Completion rates and engagement by module</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span>Maturity Assessment</span>
                    <span className="font-medium">85%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full" style={{ width: '85%' }}></div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span>Value Assessment</span>
                    <span className="font-medium">73%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-green-600 h-2 rounded-full" style={{ width: '73%' }}></div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span>ROI Analysis</span>
                    <span className="font-medium">91%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-purple h-2 rounded-full" style={{ width: '91%' }}></div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>User Engagement Trends</CardTitle>
                <CardDescription>Assessment activity over time</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8">
                  <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    Analytics Dashboard
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Detailed engagement analytics and trend visualizations coming soon
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}